import { ref, computed } from "vue";
import { defineStore } from "pinia";
import en from '@/languages/en.json';
import de from '@/languages/de.json';

export const useAppStore = defineStore("appStore", () => {
  const isDarkMode = ref<boolean>(true);
  const isMobile = ref<any>();
  const isMenu = ref<boolean>();
  const userLang = ref<string>(navigator.language.substring(0, 2));
  
  const heroSection = ref<HTMLElement>();
  const skillSetSection = ref<HTMLElement>();
  const devSection = ref<HTMLElement>();
  const artSection = ref<HTMLElement>();
  const contactSection = ref<HTMLElement>();

  const brandIcon = computed(() => { return 'mdi-human-greeting'; });
  const siteName = computed(() => { return 'Cory Butler'; });

  const certs = ref<any>({
    axelos_itil: {
      title: 'Axelos ITIL Foundation Certification',
      icon: 'mdi-account-group'
    },
    aws_cloudPrac: {
      title: 'AWS Certified Cloud Practitioner',
      icon: 'mdi-aws'
    },
    comptia_projPlus: {
      title: 'CompTIA Project+ Certification',
      icon: 'mdi-chart-arc'
    },
    pmi_capmCourse: {
      title: 'PMI Course: Project Management (CAPM)',
      icon: 'mdi-chart-areaspline'
    },
    skillsets_msCopilot: {
      title: 'Skillsets Course: Microsoft 365 Copilot',
      icon: 'mdi-microsoft'
    },
    wgu_frontEndDev: {
      title: 'WGU Certificate: Front-End Developer',
      icon: 'mdi-code-block-tags'
    }
  });

  const skills = ref<any>({
    ai_prompting: {
      title: 'AI Prompting',
      icon: 'mdi-message-cog'
    },
    csharp: {
      title: 'C#',
      icon: 'mdi-language-csharp'
    },
    css: {
      title: 'CSS',
      icon: 'mdi-language-css3'
    },
    dotnet: {
      title: '.NET',
      icon: 'mdi-dot-net'
    },
    english: {
      title: 'English (native)',
      icon: 'mdi-numeric-1-circle'
    },
    excel: {
      title: 'Excel',
      icon: 'mdi-microsoft-excel'
    },
    express: {
      title: 'Express',
      icon: 'mdi-server-network'
    },
    german: {
      title: 'German (B2)',
      icon: 'mdi-numeric-2-circle'
    },
    github: {
      title: 'GitHub',
      icon: 'mdi-github'
    },
    github_copilot: {
      title: 'Copilot',
      icon: 'mdi-github'
    },
    html: {
      title: 'HTML',
      icon: 'mdi-language-html5'
    },
    illustrator: {
      title: 'Illustrator',
      icon: 'mdi-vector-square-edit'
    },
    javascript: 
    {
      title: 'JavaScript',
      icon: 'mdi-language-javascript'
    },
    krpano: {
      title: 'krpano',
      icon: 'mdi-rotate-360'
    },
    laravel: {
      title: 'Laravel',
      icon: 'mdi-laravel'
    },
    ms_copilot: {
      title: 'MS Copilot',
      icon: 'mdi-microsoft'
    },
    mysql: {
      title: 'MySQL Workbench',
      icon: 'mdi-dolphin'
    },
    nuxt: {
      title: 'Nuxt',
      icon: 'mdi-nuxt'
    },
    openai: {
      title: 'OpenAI',
      icon: 'mdi-cloud-braces'
    },
    outlook: {
      title: 'Outlook',
      icon: 'mdi-microsoft-outlook'
    },
    phaser: {
      title: 'Phaser',
      icon: 'mdi-rocket-launch'
    },
    photoshop: {
      title: 'Photoshop',
      icon: 'mdi-image-edit'
    },
    phpmyadmin: {
      title: 'phpMyAdmin',
      icon: 'mdi-sail-boat'
    },
    premiere: {
      title: 'Premiere',
      icon: 'mdi-movie-open'
    },
    postgresql: {
      title: 'PostgreSQL',
      icon: 'mdi-elephant'
    },
    react: {
      title: 'React',
      icon: 'mdi-react'
    },
    sass: {
      title: 'Sass',
      icon: 'mdi-sass'
    },
    supabase: {
      title: 'Supabase',
      icon: 'mdi-database'
    },
    typescript: 
    {
      title: 'TypeScript',
      icon: 'mdi-language-typescript'
    },
    vercel: {
      title: 'Vercel',
      icon: 'mdi-triangle'
    },
    vimeoApi: {
      title: 'Vimeo API',
      icon: 'mdi-vimeo'
    },
    visualstudio: {
      title: 'Visual Studio',
      icon: 'mdi-microsoft-visual-studio'
    },
    vite: {
      title: 'Vite',
      icon: 'mdi-flash'
    },
    vscode: {
      title: 'VS Code',
      icon: 'mdi-microsoft-visual-studio-code'
    },
    vue: 
    {
      title: 'Vue',
      icon: 'mdi-vuejs'
    },
    vuetify: 
    {
      title: 'Vuetify',
      icon: 'mdi-vuetify'
    },
    webpack: {
      title: 'Webpack',
      icon: 'mdi-webpack'
    },
    word: {
      title: 'Word',
      icon: 'mdi-microsoft-word'
    },
    xml: {
      title: 'XML',
      icon: 'mdi-xml'
    }
  });

  const txt = computed(() => { 
    switch (userLang.value) {
      case 'de': return { ...de };
      default: return { ...en };
    }
  });
  
  const langs = ref<Array<any>>([
    { id: 'de', name: txt.value.lang.german },
    { id: 'en', name: txt.value.lang.english }
  ]);

  return {
    brandIcon, certs, isDarkMode, isMenu, isMobile, langs, siteName, skills, txt, userLang,
    heroSection, skillSetSection, devSection, artSection, contactSection
  };
},
{
  persist: {
    omit: ['certs', 'isMobile', 'isMenu', 'langs', 'skills', 'txt', 'heroSection', 'skillSetSection', 'devSection', 'artSection', 'contactSection']
  }
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAppStore, import.meta.hot))
}